import {
  Callout,
  DirectionalHint,
  IconButton,
  PrimaryButton,
  Stack,
  Text,
  TextField,
  TooltipHost,
} from '@fluentui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useId } from '@fluentui/react-hooks';
import { StandardCalculationType } from 'common/types/globalTypes';
import { useLazyQuery } from '@apollo/client';
import {
  DateCalcTermDays,
  DateCalcTermDaysVariables,
} from 'common/graphql/__generated__/DateCalcTermDays';
import {
  dateCalcTermWeeks,
  dateCalcTermWeeksVariables,
} from 'common/graphql/__generated__/DateCalcTermWeeks';
import {
  DateCalcTermMonths,
  DateCalcTermMonthsVariables,
} from 'common/graphql/__generated__/DateCalcTermMonths';
import NumberFormat from 'react-number-format';
import { useCommonStyles } from 'common/styles';
import { PurchaseOrderCommonData_companyPurchaseOrderTypes_nodes_companyPurchaseOrderItemTypes_nodes_unitOfMeasureGroup_unitOfMeasureItemsByUnitOfMeasureGroupId_nodes } from 'purchaseOrder/view/__generated__/PurchaseOrderCommonData';
import {
  DateCalcTermNights,
  DateCalcTermNightsVariables,
} from 'common/graphql/__generated__/DateCalcTermNights';

import DATE_CALC_DAYS from 'common/graphql/DateCalcTermDays.graphql';
import DATE_CALC_MONTHS from 'common/graphql/DateCalcTermMonths.graphql';
import DATE_CALC_WEEKS from 'common/graphql/DateCalcTermWeeks.graphql';
import DATE_CALC_NIGHTS from 'common/graphql/DateCalcTermNights.graphql';

export type RateFrequencyProp =
  PurchaseOrderCommonData_companyPurchaseOrderTypes_nodes_companyPurchaseOrderItemTypes_nodes_unitOfMeasureGroup_unitOfMeasureItemsByUnitOfMeasureGroupId_nodes;

interface EndDateCalculatorProps {
  startDate: string;
  calculationType: StandardCalculationType | null | undefined;
  abbreviation?: string | null | undefined;
  onEndDateReturn: (endDate: string, term?: string | undefined) => void;
  disabled: boolean;
  label?: string;
}
export const EndDateCalculator: React.FC<EndDateCalculatorProps> = ({
  startDate,
  calculationType,
  onEndDateReturn,
  abbreviation,
  disabled,
  label = 'Calculate end date',
}) => {
  const commonStyles = useCommonStyles();
  const [visible, setVisible] = useState(false);
  const [hint, setHint] = useState<string>('');
  const buttonId = useId('OverrideApprovalHint');
  const [term, setTerm] = useState<string | undefined>();
  const getHint = useCallback(() => {
    switch (calculationType) {
      case 'PO_DAILY_NIGHTLY':
      case 'PO_DAILY':
        setHint('Enter Days');
        break;
      case 'PO_WEEK_7_DAY':
      case 'PO_3_DAY_WEEK_7_DAY':
      case 'PO_WEEK_5_DAY':
      case 'PO_BI_WEEK_7_DAY':
      case 'PO_BI_WEEK_5_DAY':
        setHint('Enter Weeks');
        break;
      case 'PO_MONTH_30_DAYS':
        setHint('Enter Months');
        break;
    }
  }, [calculationType]);

  useEffect(() => {
    getHint();
  }, [getHint]);

  const [getDaysEndDate] = useLazyQuery<
    DateCalcTermDays,
    DateCalcTermDaysVariables
  >(DATE_CALC_DAYS, {
    fetchPolicy: 'network-only',
    onCompleted(data) {
      if (data.dateCalcTermDays) onEndDateReturn(data.dateCalcTermDays, term);
    },
  });

  const [getWeeksEndDate] = useLazyQuery<
    dateCalcTermWeeks,
    dateCalcTermWeeksVariables
  >(DATE_CALC_WEEKS, {
    fetchPolicy: 'network-only',
    onCompleted(data) {
      if (data.dateCalcTermWeeks) onEndDateReturn(data.dateCalcTermWeeks, term);
    },
  });

  const [getMonthsEndDate] = useLazyQuery<
    DateCalcTermMonths,
    DateCalcTermMonthsVariables
  >(DATE_CALC_MONTHS, {
    fetchPolicy: 'network-only',
    onCompleted(data) {
      if (data.dateCalcTermMonths)
        onEndDateReturn(data.dateCalcTermMonths, term);
    },
  });

  const [getDaysNightEndDate] = useLazyQuery<
    DateCalcTermNights,
    DateCalcTermNightsVariables
  >(DATE_CALC_NIGHTS, {
    fetchPolicy: 'network-only',
    onCompleted(data) {
      if (data.dateCalcTermNights)
        onEndDateReturn(data.dateCalcTermNights, term);
    },
  });

  const onCalculate = async () => {
    const termInputNumber = parseInt(term || '0');
    if (termInputNumber) {
      switch (calculationType) {
        case 'PO_DAILY_NIGHTLY':
          getDaysNightEndDate({
            variables: { startDate, days: termInputNumber },
          });
          break;
        case 'PO_DAILY':
        case 'PO_MULTI_DAY':
          getDaysEndDate({
            variables: { startDate, days: termInputNumber },
          });
          break;
        case 'PO_WEEK_7_DAY':
        case 'PO_3_DAY_WEEK_7_DAY':
        case 'PO_WEEK_5_DAY':
        case 'PO_BI_WEEK_7_DAY':
        case 'PO_BI_WEEK_5_DAY':
          getWeeksEndDate({ variables: { startDate, weeks: termInputNumber } });
          break;
        default:
          getMonthsEndDate({
            variables: { startDate, months: termInputNumber },
          });
          break;
      }
      setVisible(false);
    }
  };

  if (calculationType && startDate)
    return (
      <Stack>
        <IconButton
          iconProps={{ iconName: 'CalendarSettings' }}
          ariaLabel="Collapse"
          style={{ marginTop: 26 }}
          id={buttonId}
          disabled={disabled}
          onClick={() => setVisible(true)}
        />
        {visible && (
          <Callout
            gapSpace={0}
            target={`#${buttonId}`}
            directionalHint={DirectionalHint.topCenter}
            onDismiss={() => setVisible(false)}
          >
            <Stack
              tokens={{
                padding: '10px 20px',
                childrenGap: 10,
              }}
            >
              <Text className={commonStyles.bold} variant="mediumPlus">
                {label}
              </Text>
              <TooltipHost content={hint}>
                <NumberFormat
                  style={{ width: 200 }}
                  decimalScale={0}
                  value={term || ''}
                  customInput={TextField}
                  placeholder={abbreviation || ''}
                  onValueChange={(valuesObject, info) => {
                    if (info.source === 'event') setTerm(valuesObject?.value);
                  }}
                />
              </TooltipHost>

              <Stack horizontal verticalAlign="center">
                <PrimaryButton
                  text="Calculate"
                  onClick={onCalculate}
                  styles={{ root: { width: 100 } }}
                />
              </Stack>
            </Stack>
          </Callout>
        )}
      </Stack>
    );
  else return null;
};
